import { SVGS } from '@/assets/svgs'
import { ActionMenu, UserAvatar } from '@/components'
import { RoundedFilledButton, RoundedOutlineButton } from '@/components/StyledComponents'
import { useConnectionBlock, useConnectionDelete } from '@/hooks/api/dashboard'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { CBox, RBox } from '@/pages/__styled'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { ConnectionModel, ConnListAction, MyConnection, SearchConnection } from '@/types'
import {
  Block,
  Check,
  Close,
  MoreVert,
  RemoveCircle,
  RoundedCornerOutlined,
  SmartButtonRounded,
} from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import SVG from 'react-inlinesvg'
import { useLocation, useNavigate } from 'react-router-dom'
import { shallow } from 'zustand/shallow'

interface Props {
  user: ConnectionModel
  onUserAction: (user: ConnectionModel, action: ConnListAction) => void
}

export default function ConnectionGridItem({ user, onUserAction }: Props) {
  const [ewUser] = useLocalStorage('ewUser', null)
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const { pathname } = useLocation()
  const connID = query.get('connID')
  const blockUser = useConnectionBlock()
  const removeUser = useConnectionDelete()
  const [searchConnections] = useAppStore((state) => [state.user.searchConnections], shallow)
  const { setSearchConnections } = useAppStore()

  useEffect(() => {
    if (removeUser.isSuccess || blockUser.isSuccess) {
      if (searchConnections[user.userID]) {
        setSearchConnections({
          ...searchConnections,
          [user.userID]: {
            ...searchConnections[user.userID],
            isConnection: false,
          },
        })
      }
    }
  }, [removeUser.isSuccess, blockUser.isSuccess])

  const onClickRemoveConnect = () => {
    toast.promise(removeUser.mutateAsync(user.userID), {
      loading: 'Removing user...',
      success: <Typography variant='p'>{`User has been removed.`}</Typography>,
      error: <Typography variant='p'>Failed to remove a user.</Typography>,
    })
  }
  const onClickBlockConnect = () => {
    toast.promise(blockUser.mutateAsync(user.userID), {
      loading: 'Blocking user...',
      success: <Typography variant='p'>{`User has been blocked.`}</Typography>,
      error: <Typography variant='p'>Failed to block a user.</Typography>,
    })
  }
  const onClickSharedGroups = () => {}

  const onClickConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onUserAction(user, ConnListAction.FOLLOW)
  }

  const onClickAcceptRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onUserAction(user, ConnListAction.ACCEPT_CONN_REQUEST)
  }

  const onClickDeclineRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onUserAction(user, ConnListAction.REJECT_CONN_REQUEST)
  }

  const topicActions = useMemo(() => {
    return [
      {
        icon: <RemoveCircle />,
        name: 'Disconnect',
        onClick: onClickRemoveConnect,
      },
      {
        icon: <Block />,
        name: 'Block',
        onClick: onClickBlockConnect,
      },
      {
        icon: <SVG src={SVGS.FilterPrivacyConnection} />,
        name: 'Shared Groups',
        onClick: onClickSharedGroups,
      },
    ]
  }, [])

  const openUserProfile = () => {
    let previewQuery = new URLSearchParams()
    previewQuery.set('id', user.userID)
    navigate(`/profile/user?${previewQuery.toString()}`)
  }

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent={'center'}
      flexDirection={'column'}
      p={3}
      onClick={openUserProfile}
      sx={{
        cursor: 'pointer',
        width: '280px',
        height: '240px',
        border: `1px solid #f1f5f9`,
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',

        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)',
          borderColor: '#e2e8f0',
        },
      }}
    >
      <CBox sx={{ alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <UserAvatar
          name={`${user?.firstName} ${user?.lastName}`}
          url={user.profilePicURL}
          size={72}
          borderRadius={100}
        />
        <CBox
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            textAlign: 'center',
          }}
        >
          <Typography
            fontSize='16px'
            fontWeight={600}
            lineHeight='20px'
            color='#111827'
            fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            letterSpacing='-0.01em'
            className='username'
          >
            {user.firstName} {user.lastName} {user.userID === ewUser.userID ? '(You)' : ''}
          </Typography>
          {user.profession?.length > 0 && (
            <Typography
              fontSize='14px'
              fontWeight={400}
              lineHeight='18px'
              color='#6b7280'
              fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              sx={{ mt: '2px' }}
            >
              {user.profession}
            </Typography>
          )}
        </CBox>
      </CBox>

      <RBox sx={{ alignItems: 'center', justifyContent: 'center', gap: 1, mt: '16px' }}>
        {/* <RoundedOutlineButton sx={{ padding: '6px 22.5px' }}>{'Profile'}</RoundedOutlineButton>

        {user.isConnection && (
          <ActionMenu id='topic-item-menu' parent={<MoreVert />} actions={topicActions} />
        )}
        {!user.isConnection && user.reqReceived && (
          <>
            <IconButton onClick={onClickAcceptRequest} className='accept-icon-btn'>
              <Check width={24} height={24} />
            </IconButton>
            <IconButton
              onClick={onClickDeclineRequest}
              className='reject-icon-btn'
              sx={{ marginLeft: 1 }}
            >
              <Close width={24} height={24} />
            </IconButton>
          </>
        )}
        {!user.isConnection && user.reqSent && (
          <Button variant='text' disabled>
            Pending
          </Button>
        )}
        {!user.isConnection && !user.reqReceived && !user.reqSent && (
          <RoundedFilledButton sx={{ padding: '6px 22.5px' }} onClick={onClickConnect}>
            {'Follow'}
          </RoundedFilledButton>
        )} */}

        <RoundedOutlineButton
          sx={{
            padding: '8px 24px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '12px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            letterSpacing: '-0.01em',
            border: '1px solid #e5e7eb',
            color: '#374151',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

            '&:hover': {
              backgroundColor: colors.meshdomBlue,
              borderColor: colors.meshdomBlue,
              color: '#ffffff',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
            },
          }}
          onClick={onClickConnect}
        >
          Follow
        </RoundedOutlineButton>
      </RBox>
    </Box>
  )
}
